/**
 * ProfileEditDialog — modale per modificare email / telefono dell'utente.
 *
 * Replica del flow web v1 (`FormEmail.js` / `FormPhone.js`):
 *  - email: 2 campi (nuova + conferma) con check `@` `.`
 *  - telefono: 2 campi con prefisso internazionale (IT default) tramite
 *    `react-international-phone` (equivalente moderno di MuiTelInput v1)
 *  - mutation `updateCliente({ email })` o `updateCliente({ phone })`
 *  - feedback di successo inline; chiusura sempre manuale (bottone Chiudi)
 *  - NB: aggiorna solo l'anagrafica Enermed (DB backend), NON Cognito.
 *    Per cambiare email/phone in Cognito (utili per MFA) serve un flow
 *    OTP separato (vedi SecuritySettings mobile).
 */

import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle2, Mail, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useUpdateCliente } from '@/lib/graphql/hooks'
import { PhoneInput } from './PhoneInput'

type EditMode = 'email' | 'phone'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: EditMode
  codiceCliente: string
}

export function ProfileEditDialog({
  open,
  onOpenChange,
  mode,
  codiceCliente,
}: Props) {
  const mutation = useUpdateCliente()

  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [confirmPhone, setConfirmPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Reset campi all'apertura del dialog.
  // NB: i campi partono SEMPRE vuoti (anche se l'utente ha già un valore
  // salvato), allineato al pattern v1 (FormEmail/FormPhone). Se pre-fillassimo
  // col vecchio valore l'utente potrebbe inviare lo stesso senza accorgersene
  // → backend riceve la stessa stringa e sembra "non aggiorna mai".
  useEffect(() => {
    if (!open) return
    setError(null)
    setSuccess(false)
    setEmail('')
    setConfirmEmail('')
    setPhone('')
    setConfirmPhone('')
  }, [open, mode])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    try {
      if (mode === 'email') {
        if (!email || !confirmEmail) {
          setError('Compila correttamente entrambi i campi.')
          return
        }
        if (email !== confirmEmail) {
          setError('Le due email non coincidono.')
          return
        }
        if (
          !email.includes('@') ||
          !email.includes('.') ||
          !confirmEmail.includes('@') ||
          !confirmEmail.includes('.')
        ) {
          setError('Il formato della email non è corretto.')
          return
        }
        await mutation.mutateAsync({
          codice_cliente: codiceCliente,
          vals: { email },
        })
      } else {
        // Sanitize: rimuovi spazi e caratteri non numerici/+ (Cognito e
        // backend si aspettano E.164: "+393453184605" senza spazi).
        const cleanPhone = phone.replace(/[^\d+]/g, '')
        const cleanConfirm = confirmPhone.replace(/[^\d+]/g, '')
        if (!cleanPhone || !cleanConfirm || cleanPhone.length <= 5 || cleanConfirm.length <= 5) {
          setError('Compila correttamente entrambi i campi.')
          return
        }
        if (cleanPhone !== cleanConfirm) {
          setError('I due numeri di cellulare non corrispondono.')
          return
        }
        if (!cleanPhone.startsWith('+') || !cleanConfirm.startsWith('+')) {
          setError('Imposta il prefisso internazionale.')
          return
        }
        // Backend usa il campo `mobile` (non `phone`) sull'input ClienteUpdate.
        await mutation.mutateAsync({
          codice_cliente: codiceCliente,
          vals: { phone: cleanPhone },
        })
      }

      setSuccess(true)
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message
          : 'Si è verificato un errore, riprova.'
      setError(msg)
    }
  }

  const closable = !mutation.isPending

  return (
    <Dialog open={open} onOpenChange={(o) => closable && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <div
            className={
              success
                ? 'mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-success/15 text-success'
                : 'mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-secondary/15 text-secondary'
            }
          >
            {success ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : mode === 'email' ? (
              <Mail className="h-5 w-5" />
            ) : (
              <Phone className="h-5 w-5" />
            )}
          </div>
          <DialogTitle>
            {success
              ? mode === 'email'
                ? 'Email aggiornata'
                : 'Numero aggiornato'
              : mode === 'email'
                ? 'Modifica email'
                : 'Modifica numero di cellulare'}
          </DialogTitle>
          <DialogDescription>
            {success
              ? mode === 'email'
                ? 'La tua email è stata aggiornata correttamente.'
                : 'Il tuo numero di cellulare è stato aggiornato correttamente.'
              : mode === 'email'
                ? 'Inserisci la nuova email due volte per confermarla.'
                : 'Inserisci il nuovo numero di cellulare due volte per confermarlo.'}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <DialogFooter className="mt-2">
            <Button onClick={() => onOpenChange(false)}>Chiudi</Button>
          </DialogFooter>
        ) : (
          <form onSubmit={onSubmit} className="mt-2 flex flex-col gap-4">
            {mode === 'email' ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input
                    id="profile-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-email-confirm">Conferma email</Label>
                  <Input
                    id="profile-email-confirm"
                    type="email"
                    autoComplete="email"
                    required
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-phone">Numero di cellulare</Label>
                  <PhoneInput id="profile-phone" value={phone} onChange={setPhone} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-phone-confirm">Conferma numero</Label>
                  <PhoneInput
                    id="profile-phone-confirm"
                    value={confirmPhone}
                    onChange={setConfirmPhone}
                  />
                </div>
              </>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Spinner className="text-secondary-foreground" />
                ) : (
                  'Conferma'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
